import { AnalyticsChartCard } from '@/components/ui/analytics/AnalyticsChartCard';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import { Badge } from '@/components/ui/shadcn/badge';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { PROJECT_STATUS_COLORS, statusColor, TASK_STATUS_COLORS } from '@/lib/chart-colors';
import { settingLabel } from '@/lib/status-labels';
import { formatCurrency } from '@/lib/utils';
import type { ProjectsAnalyticsData, StatusCountRow } from '@/types/analytics';
import { CalendarClock, FolderKanban, Timer, Wallet } from 'lucide-react';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, XAxis, YAxis } from 'recharts';

interface Props {
    statusCounts: Record<string, number>;
    financials: { total_budget: number; total_costs: number };
    projectsAnalytics?: ProjectsAnalyticsData;
}

export default function ProjectsAnalytics({ statusCounts, financials, projectsAnalytics }: Props) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const statusHeightClass = useChartHeightClass('md');
    const trendHeightClass = isMobile ? 'h-[220px]' : 'h-[280px]';

    const kpis = useMemo(() => {
        const total = Object.values(statusCounts ?? {}).reduce((sum, v) => sum + Number(v), 0);
        const projectRevenue = Number(financials?.total_budget ?? 0);
        const projectCosts = Number(financials?.total_costs ?? 0);
        const avgCycle = Number(projectsAnalytics?.avg_cycle_time_days ?? 0);
        const overdueCount = Number(projectsAnalytics?.overdue_projects_count ?? 0);
        return { total, projectRevenue, projectCosts, avgCycle, overdueCount };
    }, [statusCounts, financials, projectsAnalytics]);

    // Status overview — colored bars
    const statusOverview = useMemo(() => {
        const withLabel = (status: string, total: number) => ({ status, total, label: settingLabel(t, 'project_statuses', status) });
        const fromDistribution: StatusCountRow[] = projectsAnalytics?.project_status_distribution ?? [];
        if (fromDistribution.length > 0) {
            return fromDistribution.map((r) => withLabel(r.status, r.count));
        }
        return Object.entries(statusCounts ?? {}).map(([status, total]) => withLabel(status, total));
    }, [statusCounts, projectsAnalytics, t]);

    // Task status split (donut)
    const taskStatusLabel = (status: string) => settingLabel(t, 'project_task_statuses', status);
    const taskStatusData = useMemo(
        () =>
            (projectsAnalytics?.task_distribution ?? []).map((row) => ({
                name: taskStatusLabel(row.status),
                value: row.count,
                fill: statusColor(TASK_STATUS_COLORS, row.status),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [projectsAnalytics],
    );
    const taskStatusConfig = useMemo(() => {
        const cfg: Record<string, { label: string; color: string }> = {};
        taskStatusData.forEach((entry) => {
            cfg[entry.name] = { label: entry.name, color: entry.fill };
        });
        return cfg;
    }, [taskStatusData]);

    // Overdue projects trend (line chart)
    const overdueProjectsTrend = projectsAnalytics?.overdue_projects_trend ?? [];

    // On-time vs late trend for completed projects (line chart)
    const onTimeVsLateTrend = projectsAnalytics?.on_time_vs_late_trend ?? [];

    // Overdue projects list
    const overdueList = projectsAnalytics?.overdue_projects_list ?? [];

    const hasOverdueTrend = overdueProjectsTrend.some((r) => (r.count ?? 0) > 0);
    const hasOnTimeTrend = onTimeVsLateTrend.some((r) => (r.on_time ?? 0) > 0 || (r.late ?? 0) > 0);

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold">{t('analytics.labels.page_4_projects_dashboard', 'Projects Dashboard')}</h2>
                <p className="text-muted-foreground text-sm">
                    {t('analytics.labels.delivery_timelines', 'Delivery timelines, margins, workload and overdue execution risk.')}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <SummaryMetricCard
                    docKey="projects.projects_count"
                    title={t('analytics.labels.projects', 'Projects')}
                    value={kpis.total}
                    subtitle={t('analytics.labels.in_selected_range', 'In selected range')}
                    icon={<FolderKanban className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="projects.project_revenue"
                    title={t('analytics.labels.project_revenue', 'Project Revenue')}
                    value={formatCurrency(kpis.projectRevenue)}
                    subtitle={t('analytics.labels.sales_value_from_projects', 'Sales value from projects')}
                    icon={<Wallet className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="projects.avg_cycle_time"
                    title={t('analytics.labels.avg_cycle_time', 'Avg Cycle Time')}
                    value={`${kpis.avgCycle.toFixed(1)}d`}
                    subtitle={t('analytics.labels.start_to_completion', 'Start to completion')}
                    icon={<CalendarClock className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="projects.overdue_projects"
                    title={t('analytics.labels.overdue_projects', 'Overdue Projects')}
                    value={kpis.overdueCount}
                    subtitle={t('analytics.labels.projects_with_overdue_tasks', 'Projects with at least 1 task past deadline')}
                    icon={<Timer className="h-4 w-4" />}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Project Status Overview — colored bar chart */}
                <AnalyticsChartCard
                    docKey="projects.project_status_overview"
                    title={t('analytics.labels.project_status_overview', 'Project Status Overview')}
                >
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.project_status_overview', 'Project Status Overview')}
                        config={{ total: { label: t('analytics.labels.projects', 'Projects'), color: '#2563eb' } }}
                        className={`${statusHeightClass} w-full`}
                    >
                        <BarChart data={statusOverview}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                                {statusOverview.map((entry, i) => (
                                    <Cell key={i} fill={statusColor(PROJECT_STATUS_COLORS, entry.status)} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ChartContainer>
                </AnalyticsChartCard>

                {/* Task Status Split — donut */}
                <AnalyticsChartCard
                    docKey="projects.task_status_split"
                    title={t('analytics.labels.task_status_split', 'Task Status Split')}
                    description={t('analytics.labels.task_status_split_desc', 'Breakdown of project tasks by status in the selected period.')}
                >
                    {taskStatusData.length > 0 ? (
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.task_status_split', 'Task Status Split')}
                            config={taskStatusConfig}
                            className={`${statusHeightClass} w-full`}
                        >
                            <PieChart>
                                <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                                <Legend
                                    layout="vertical"
                                    align="right"
                                    verticalAlign="middle"
                                    iconType="circle"
                                    formatter={(value) => <span className="text-sm">{value}</span>}
                                />
                                <Pie data={taskStatusData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={100} paddingAngle={2}>
                                    {taskStatusData.map((entry, i) => (
                                        <Cell key={i} fill={entry.fill} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ChartContainer>
                    ) : (
                        <div className={`text-muted-foreground flex ${statusHeightClass} items-center justify-center text-sm`}>
                            {t('analytics.labels.no_tasks', 'No tasks in this period')}
                        </div>
                    )}
                </AnalyticsChartCard>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Overdue Projects trend — line chart */}
                <AnalyticsChartCard
                    docKey="projects.overdue_projects_trend"
                    title={t('analytics.labels.overdue_projects', 'Overdue Projects')}
                    description={t(
                        'analytics.labels.projects_with_tasks_past_deadline',
                        'Projects with at least 1 task beyond the project end date, by start period.',
                    )}
                >
                    {hasOverdueTrend ? (
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.overdue_projects', 'Overdue Projects')}
                            config={{ count: { label: t('analytics.labels.projects', 'Projects'), color: '#ef4444' } }}
                            className={`${trendHeightClass} w-full`}
                        >
                            <LineChart data={overdueProjectsTrend}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                <ChartTooltip content={<ChartTooltipContent />} />
                                <Line dataKey="count" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ChartContainer>
                    ) : (
                        <div className={`text-muted-foreground flex ${trendHeightClass} items-center justify-center text-sm`}>
                            {t('analytics.labels.no_overdue_projects', 'No overdue projects in this period')}
                        </div>
                    )}
                </AnalyticsChartCard>

                {/* On-time vs Late (completed only) — line chart */}
                <AnalyticsChartCard
                    docKey="projects.on_time_vs_late"
                    title={t('analytics.labels.on_time_vs_late', 'On-time vs Late')}
                    description={t(
                        'analytics.labels.completed_projects_on_time_vs_late',
                        'Completed projects: on time vs those that had overdue tasks.',
                    )}
                >
                    {hasOnTimeTrend ? (
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.on_time_vs_late', 'On-time vs Late')}
                            config={{
                                on_time: { label: t('analytics.labels.on_time', 'On time'), color: '#22c55e' },
                                late: { label: t('analytics.labels.late', 'Late'), color: '#ef4444' },
                            }}
                            className={`${trendHeightClass} w-full`}
                        >
                            <LineChart data={onTimeVsLateTrend}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                <ChartTooltip content={<ChartTooltipContent />} />
                                <ChartLegend content={<ChartLegendContent />} />
                                <Line dataKey="on_time" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                                <Line dataKey="late" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ChartContainer>
                    ) : (
                        <div className={`text-muted-foreground flex ${trendHeightClass} items-center justify-center text-sm`}>
                            {t('analytics.labels.no_completed_projects', 'No completed projects with end date in this period')}
                        </div>
                    )}
                </AnalyticsChartCard>
            </div>

            {/* Overdue Tasks / Projects list */}
            <AnalyticsChartCard
                docKey="projects.overdue_tasks_projects"
                title={t('analytics.labels.overdue_tasks_projects', 'Overdue Tasks / Projects')}
                description={t(
                    'analytics.labels.projects_with_overdue_tasks_desc',
                    'Projects that have at least one task exceeding the project timeline.',
                )}
            >
                {overdueList.length > 0 ? (
                    <div className="space-y-2">
                        {overdueList.map((r, i) => (
                            <div key={`${r.name ?? 'project'}-${i}`} className="flex items-center justify-between rounded border p-2 text-sm">
                                <span className="font-medium">{r.name ?? t('analytics.labels.item', 'Project')}</span>
                                <div className="flex items-center gap-2">
                                    {r.end_date && (
                                        <span className="text-muted-foreground text-xs">
                                            {t('analytics.labels.deadline', 'Deadline:')} {r.end_date}
                                        </span>
                                    )}
                                    <Badge
                                        variant="outline"
                                        style={{
                                            borderColor: statusColor(PROJECT_STATUS_COLORS, r.status),
                                            color: statusColor(PROJECT_STATUS_COLORS, r.status),
                                        }}
                                    >
                                        {settingLabel(t, 'project_statuses', r.status)}
                                    </Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-muted-foreground flex h-[120px] items-center justify-center text-sm">
                        {t('analytics.labels.no_overdue_projects', 'No overdue projects')}
                    </div>
                )}
            </AnalyticsChartCard>
        </div>
    );
}
